import { useContext } from "react";

import { useMutation } from "@tanstack/react-query";

import { AuthenticationContext } from "../context";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import { PresignedUrlPost } from "../types/documents";
import { ViewRequest, getViewRequest, request } from "./request";

export const DMR_SUBMISSION_TYPES = {
  PHARMACY: "pharmacy",
  MEDICAL: "medical",
} as const;
export const DMR_FORM_FILL_TYPES = ["pdf", "web"] as const;
export const DMR_PHARMACY_DETAILS_REASONS = [
  "id_card_unavailable",
  "out_of_network_due_to_travel",
  "out_of_network_due_to_vaccine",
  "out_of_network_due_to_no_network_pharmacy",
  "out_of_network_due_to_federal_emergency",
  "out_of_network_due_to_out_of_stock",
  "purchased_while_awaiting_approval",
  "pharmacy_billed_incorrect_insurance",
  "compound_prescription",
  "coordination_of_benefits",
  "received_vaccine_in_outpatient_setting",
  "other",
] as const;
export type DirectMemberReimbursementSubmissionType = (typeof DMR_SUBMISSION_TYPES)[keyof typeof DMR_SUBMISSION_TYPES];
export type DirectMemberReimbursementFormFillType = (typeof DMR_FORM_FILL_TYPES)[number];
export type DirectMemberReimbursementPharmacyDetailsReason = (typeof DMR_PHARMACY_DETAILS_REASONS)[number];

export interface DirectMemberReimbursementDocument {
  id?: number;
  file_name?: string;
  name?: string;
  description?: string;
  document_type?: string;
  readonly bucket?: string;
  readonly key?: string;
  readonly post?: PresignedUrlPost;
}

export interface DirectMemberReimbursementSubmissionMedical {
  claim_form: DirectMemberReimbursementDocument;
}

export interface DirectMemberReimbursementCompoundDetails {
  ndc: string;
  quantity: number;
  cost: number;
}

export interface DirectMemberReimbursementCompoundPrescriptionInformation {
  rx_number: string;
  day_supply: number;
  compound_details: DirectMemberReimbursementCompoundDetails[];
}

export interface DirectMemberReimbursementCoordinationOfBenefits {
  on_job_inquiry: boolean;
  other_insurance_plan: boolean;
  employer_insurance_name: string;
  id_number: string;
  phone_number: string;
  primary_carriers_explanation_of_benefits: Partial<DirectMemberReimbursementDocument>[];
}

export interface DirectMemberReimbursementVaccineInformation {
  multiple_dose: boolean;
  dose_series?: number;
  administered_location: string;
}

export interface DirectMemberReimbursementPharmacyPrescription {
  ndc: string;
  date_of_fill: string;
  rx_number: string;
  total_paid: number;
  admin_fee: number;
}

export interface DirectMemberReimbursementPharmacyDetails {
  reasons: DirectMemberReimbursementPharmacyDetailsReason[];
  specific_reason?: string;
  pharmacy_npi: string;
  prescriber_npi: string;
  prescription: DirectMemberReimbursementPharmacyPrescription;
  compound_prescription_information?: DirectMemberReimbursementCompoundPrescriptionInformation;
  coordination_of_benefits?: DirectMemberReimbursementCoordinationOfBenefits;
  vaccine_information?: DirectMemberReimbursementVaccineInformation;
}

export interface DirectMemberReimbursementSubmissionPharmacy {
  claim_form?: DirectMemberReimbursementDocument;
  claim_evidence_documents: DirectMemberReimbursementDocument[];
  pharmacy_details?: DirectMemberReimbursementPharmacyDetails;
}

export interface DirectMemberReimbursementSubmissionRequestFormData {
  medical?: DirectMemberReimbursementSubmissionMedical;
  pharmacy?: DirectMemberReimbursementSubmissionPharmacy;
}

export interface DirectMemberReimbursementSubmissionRequest {
  client_id: string;
  external_member_id: string;
  person_code: string;
  submission_type: DirectMemberReimbursementSubmissionType;
  form_fill_type?: DirectMemberReimbursementFormFillType;
  form_data: DirectMemberReimbursementSubmissionRequestFormData;
  contact_email_address: string;
  contact_phone_number: string;
}

export interface DirectMemberReimbursementSubmissionFormData {
  readonly name?: string;
  readonly email: string;
  readonly phone_number: string;
  readonly pharmacy?: {
    readonly npi: string;
    readonly name?: string;
    readonly address?: string;
    readonly zip_code?: string;
  };
  readonly prescriber?: {
    readonly npi: string;
    readonly name?: string;
  };
  readonly reasons?: DirectMemberReimbursementPharmacyDetailsReason[];
  readonly specific_reason?: string;
  readonly drug_information?: {
    readonly ndc: string;
    readonly date_of_fill?: string;
    readonly rx_number?: string;
    readonly total_paid?: string;
    readonly admin_fee?: string;
  };
  readonly compound_information?: {
    readonly rx_number: string;
    readonly days_supply: number;
    readonly compound_details: {
      readonly ndc: string;
      readonly quantity: number;
      readonly cost: number;
    }[];
  };
  readonly coordination_of_benefits?: {
    readonly on_job_injury: boolean;
    readonly other_insurance_plan: boolean;
    readonly group_insuracen_plan: {
      readonly name: string;
      readonly id_number: string;
      readonly phone_number: string;
    };
  };
  readonly vaccine_information?: {
    readonly multiple_doses: boolean;
    readonly dose_series?: number;
    readonly administered_location: "physician_office" | "clinic" | "pharmacy";
  };
}

export interface DirectMemberReimbursementSubmission extends Partial<DirectMemberReimbursementSubmissionRequest> {
  readonly id: number;
  readonly submission_status: "initiated" | "uploaded" | "processing" | "processed" | "error";
  readonly form_data: object;
  readonly confirmation_number: string;
}

export interface DirectMemberReimbursementResponse {
  readonly dmr_submission?: DirectMemberReimbursementSubmission;
}

export interface DirectMemberReimbursementSubmissionResponse extends DirectMemberReimbursementResponse {
  readonly dmr_documents: DirectMemberReimbursementDocument[];
}

export interface DirectMemberReimbursementDocumentUploadFile extends Blob {
  lastModified?: number;
  lastModifiedDate?: {};
  name?: string;
  size: number;
  type: string;
  webkitRelativePath?: string;
}

export interface DirectMemberReimbursementSubmissionProcessRequest {
  readonly id: number;
}

export const createDMRSubmission = async (
  authToken: string,
  payload: Partial<ViewRequest> & DirectMemberReimbursementSubmissionRequest,
) => {
  return request<Partial<ViewRequest> & DirectMemberReimbursementSubmissionRequest, DirectMemberReimbursementSubmissionResponse>(
    {
      endpoint: "/dmr/submission/create",
      body: { ...payload },
    },
    authToken,
  );
};

export const useCreateDMRSubmissionMutation = () => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  return useMutation({
    mutationFn: async (payload: DirectMemberReimbursementSubmissionRequest) => {
      if (isViewingDataholder) {
        return createDMRSubmission(await getAuthToken(), {
          ...payload,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return createDMRSubmission(await getAuthToken(), { ...payload });
      }
    },
  });
};

export const uploadDMRDocumentFileToS3 = async (
  uploadPost: PresignedUrlPost,
  file: DirectMemberReimbursementDocumentUploadFile,
) => {
  const { fields, url } = uploadPost;

  const formData = new FormData();

  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value as string);
  }

  formData.append("file", file);

  return request<FormData, void>({
    endpoint: url,
    body: formData,
    useAuth: false,
    removeReferrerUrlHeader: true,
  });
};

export const processDMRSubmission = async (
  authToken: string,
  payload: Partial<ViewRequest> & DirectMemberReimbursementSubmissionProcessRequest,
) => {
  return request<Partial<ViewRequest> & DirectMemberReimbursementSubmissionProcessRequest, DirectMemberReimbursementResponse>(
    {
      endpoint: "/dmr/submission/process",
      body: { ...payload },
    },
    authToken,
  );
};

export const useProcessDMRSubmissionMutation = () => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  return useMutation({
    mutationFn: async (dmrId: number) => {
      if (isViewingDataholder) {
        return processDMRSubmission(await getAuthToken(), {
          id: dmrId,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return processDMRSubmission(await getAuthToken(), { id: dmrId });
      }
    },
  });
};
